import React from "react";

const ConsentDetailsModal = ({ show, onClose, consent }) => {
  if (!show || !consent) return null;

  const getEffectiveStatus = () => {
    const now = new Date();
    const apiStatus = consent.status?.toUpperCase();
    if (consent.dataEraseAt && new Date(consent.dataEraseAt) < now)
      return "EXPIRED";
    const expiry =
      consent.approved?.expiryDate ||
      consent.consentExpiryOn ||
      consent.approved?.dataEraseAt ||
      consent.permission?.dataEraseAt;
    if (expiry && new Date(expiry) < now) return "EXPIRED";
    if (apiStatus === "REQUESTED" && consent.createdAt) {
      if (
        new Date(consent.createdAt).getTime() + 60 * 60 * 1000 <
        now.getTime()
      )
        return "EXPIRED";
    }
    if (consent.consent_expiry && new Date(consent.consent_expiry) < now)
      return "EXPIRED";
    return apiStatus || "UNKNOWN";
  };

  const status = getEffectiveStatus();
  const hasApproved =
    !!consent.approved && ["GRANTED", "REVOKED", "EXPIRED"].includes(status);

  const effectiveAccessMode =
    hasApproved && consent.approved.accessMode
      ? consent.approved.accessMode
      : consent.permission?.accessMode;
  const effectiveDateRange =
    hasApproved && consent.approved.dateRange
      ? consent.approved.dateRange
      : consent.permission?.dateRange;
  const effectiveHiTypes =
    hasApproved && consent.approved.hiTypes?.length
      ? consent.approved.hiTypes
      : consent.hiTypes || consent.permission?.hiTypes || [];
  const expiryDate =
    consent.approved?.expiryDate ||
    consent.consentExpiryOn ||
    consent.approved?.dataEraseAt ||
    consent.permission?.dataEraseAt ||
    null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadgeClass = (s) => {
    switch (s?.toUpperCase()) {
      case "GRANTED":
        return "bg-success";
      case "REQUESTED":
        return "bg-info";
      case "INITIATED":
        return "bg-secondary";
      case "EXPIRED":
        return "bg-secondary text-white";
      case "REVOKED":
        return "bg-warning text-dark";
      case "DENIED":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  };

  const formatPurpose = (rp) => {
    if (rp === "HIMS") return "Hospital Records";
    if (rp === "PHR") return "Patient Health Records";
    return rp;
  };

  const renderSection = (title, children) => (
    <div className="mb-4">
      <h6 className="text-primary border-bottom pb-2 mb-3">{title}</h6>
      <div className="ps-2">{children}</div>
    </div>
  );

  const renderField = (label, value) => (
    <div className="row mb-2">
      <div className="col-sm-4 text-muted small text-uppercase fw-bold">
        {label}
      </div>
      <div className="col-sm-8">{value || "N/A"}</div>
    </div>
  );

  const renderExpiresOn = () => {
    if (!expiryDate || ["DENIED", "INITIATED", "REQUESTED"].includes(status)) {
      return renderField("Expires On", "N/A");
    }
    if (status === "REVOKED") {
      return (
        <div className="row mb-2">
          <div className="col-sm-4 text-muted small text-uppercase fw-bold">
            Expires On
          </div>
          <div className="col-sm-8">
            {formatDate(expiryDate)}{" "}
            <span className="badge bg-warning text-dark ms-1">Revoked</span>
          </div>
        </div>
      );
    }
    if (status === "EXPIRED") {
      return (
        <div className="row mb-2">
          <div className="col-sm-4 text-muted small text-uppercase fw-bold">
            Expires On
          </div>
          <div className="col-sm-8">
            <span className="text-danger">{formatDate(expiryDate)}</span>{" "}
            <span className="badge bg-secondary text-white ms-1">Expired</span>
          </div>
        </div>
      );
    }
    return renderField("Expires On", formatDate(expiryDate));
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header bg-light">
            <h5 className="modal-title">Consent Details</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body p-4">
            {/* Patient header */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="p-3 bg-light rounded">
                  <h5 className="mb-1 text-dark">{consent.patientName}</h5>
                  <p className="mb-0 text-muted small">
                    {consent.abhaNumber && (
                      <>
                        <span className="fw-semibold">Abha:</span>{" "}
                        {consent.abhaNumber}
                      </>
                    )}
                    {consent.abhaNumber &&
                      (consent.abhaAddress || consent.patientAbhaId) && (
                        <><span className="mx-2"></span> <br/> </>
                      )}
                    {(consent.abhaAddress || consent.patientAbhaId) && (
                      <>
                        <span className="fw-semibold">Abha Address:</span>{" "}
                        {consent.abhaAddress || consent.patientAbhaId}
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div className="col-md-6 text-md-end pt-2">
                <span
                  className={`badge p-2 px-3 ${getStatusBadgeClass(status)}`}
                >
                  {status}
                </span>
                <p className="mt-2 mb-0 text-muted small">
                  Created: {formatDate(consent.createdAt)}
                </p>
              </div>
            </div>

            {/* Requester */}
            {renderSection(
              "Requester Information",
              <>
                {renderField("Name", consent.requester?.name)}
                {consent.requester?.identifier && (
                  <div className="row mb-2">
                    <div className="col-sm-4 text-muted small text-uppercase fw-bold">
                      {consent.requester.identifier.type}
                    </div>
                    <div className="col-sm-8">
                      {consent.requester.identifier.value}
                    </div>
                  </div>
                )}
              </>,
            )}

            {/* Purpose */}
            {renderSection(
              "Purpose of Request",
              <>
                {renderField("Purpose", consent.purpose?.text)}
                {renderField("Code", consent.purpose?.code)}
                {renderField(
                  "Request Purpose",
                  formatPurpose(consent.requestPurpose),
                )}
              </>,
            )}

            {/* Single consent details section */}
            {renderSection(
              "Consent Details",
              <>
                {renderField("Access Mode", effectiveAccessMode)}
                {renderField(
                  "Date Range",
                  effectiveDateRange
                    ? `${formatDate(effectiveDateRange.from)} \u2192 ${formatDate(effectiveDateRange.to)}`
                    : null,
                )}
                <div className="row mb-2">
                  <div className="col-sm-4 text-muted small text-uppercase fw-bold">
                    HI Types
                  </div>
                  <div className="col-sm-8">
                    <div className="d-flex flex-wrap gap-1">
                      {effectiveHiTypes.map((type, idx) => (
                        <span
                          key={idx}
                          className="badge bg-light text-dark border"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {renderExpiresOn()}
                {/* {consent.consentArtefacts?.length > 0 && (
                  <div className="row mb-2">
                    <div className="col-sm-4 text-muted small text-uppercase fw-bold">
                      Consent Artefacts
                    </div>
                    <div className="col-sm-8">
                      <span className="badge bg-success">
                        {consent.consentArtefacts.length} artefact
                        {consent.consentArtefacts.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                )} */}
              </>,
            )}

            {/* Timestamps & IDs */}
            {renderSection(
              "Other Details",
              <>
                {/* {renderField("Consent Request ID", consent.consentRequestId)}
                {renderField("Transaction ID", consent.requestId)} */}
                {renderField("Created", formatDate(consent.createdAt))}
                {["GRANTED", "REVOKED", "EXPIRED"].includes(status) &&
                  consent.grantedAt &&
                  renderField("Granted At", formatDate(consent.grantedAt))}
                {status === "DENIED" &&
                  consent.deniedAt &&
                  renderField("Denied At", formatDate(consent.deniedAt))}
                {status === "REVOKED" &&
                  consent.revokedAt &&
                  renderField("Revoked At", formatDate(consent.revokedAt))}
              </>,
            )}
          </div>
          <div className="modal-footer bg-light">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentDetailsModal;
